import { Session, useApiTradeV1SiteMaterialsEvidenceSessionsRetrieve } from 'api';
import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from 'ui';

import { EvidenceAiLooseSession } from '../EvidenceAiLooseSession/EvidenceAiLooseSession';
import { EvidenceAiSession } from '../EvidenceAiSession/EvidenceAiSession';
import { EvidenceInspectionSession } from '../EvidenceInspectionSession/EvidenceInspectionSession';
import { EvidenceSampleSession } from '../EvidenceSampleSession/EvidenceSampleSession';

const PAGE_SIZE = 25;

export const EvidenceSessionList = ({
  siteMaterialId,
  defaultCollapsed,
}: {
  siteMaterialId: number;
  defaultCollapsed?: boolean;
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [allSessions, setAllSessions] = useState<Session[]>([]);
  const { data: sessions, isLoading } = useApiTradeV1SiteMaterialsEvidenceSessionsRetrieve(String(siteMaterialId), {
    page: pageNumber,
    page_size: PAGE_SIZE,
  });
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sessions?.results && !isLoading) {
      if (pageNumber === 1) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setAllSessions(sessions.results);
      } else {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setAllSessions((prev) => [...prev, ...sessions.results]);
      }
    }
  }, [sessions, isLoading, pageNumber]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const hasFirstPageLoaded = allSessions.length > 0;
        if (entry.isIntersecting && !isLoading && sessions?.next && hasFirstPageLoaded) {
          setPageNumber((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loaderRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [allSessions.length, isLoading, sessions]);

  return (
    <div>
      {allSessions.length > 0 && (
        <div data-testid="sessions-list">
          {allSessions.map((session, index) => {
            if (session.session_type === 'AI') {
              return (
                <EvidenceAiSession
                  key={session.date + session.session_type + index}
                  session={session}
                  defaultExpanded={!defaultCollapsed && index === 0}
                  siteMaterialId={Number(siteMaterialId)}
                />
              );
            }

            if (session.session_type === 'SS') {
              return (
                <EvidenceSampleSession
                  key={session.date + session.session_type + index}
                  session={session}
                  defaultExpanded={!defaultCollapsed && index === 0}
                  siteMaterialId={Number(siteMaterialId)}
                />
              );
            }

            if (session.session_type === 'IN') {
              return (
                <EvidenceInspectionSession
                  key={session.date + session.session_type + index}
                  session={session}
                  defaultExpanded={!defaultCollapsed && index === 0}
                />
              );
            }

            if (session.session_type === 'AS') {
              return (
                <EvidenceAiLooseSession
                  key={session.date + session.session_type + index}
                  session={session}
                  defaultExpanded={!defaultCollapsed && index === 0}
                  siteMaterialId={Number(siteMaterialId)}
                />
              );
            }

            return null;
          })}
        </div>
      )}
      {sessions?.next ? (
        <Box
          ref={loaderRef}
          data-testid="loader-ref"
          sx={{
            height: 20,
            my: 2,
          }}
        />
      ) : null}
      {pageNumber > 1 && isLoading ? <CircularProgress /> : null}
    </div>
  );
};
